import { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { EthereumWalletConnectors } from "@dynamic-labs/ethereum";
import { DynamicContextProvider } from "@dynamic-labs/sdk-react-core";

import RoutesManager from "./routes";
import { Env } from "./common/constants";
import Loader from "./components/loader/loader";
import ViewportGuard from "./pages/viewport-guard";
import { AnalyticsEvents, capturePosthogEvent } from "./common/utils/analytics";

const App = () => {
  console.log("Environment Variables:", {
    REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL,
    REACT_APP_DYNAMIC_ENVIRONMENT_ID:
      process.env.REACT_APP_DYNAMIC_ENVIRONMENT_ID,
    REACT_APP_MEDIA_UPLOAD_URL: process.env.REACT_APP_MEDIA_UPLOAD_URL,
    REACT_APP_PAYMENT_PUBLIC_KEY: process.env.REACT_APP_PAYMENT_PUBLIC_KEY,
    REACT_APP_ALCHEMY_API_KEY: process.env.REACT_APP_ALCHEMY_API_KEY,
    REACT_APP_ALCHEMY_POLICY_ID: process.env.REACT_APP_ALCHEMY_POLICY_ID,
    REACT_APP_ALCHEMY_RPC_URL: process.env.REACT_APP_ALCHEMY_RPC_URL,
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    REACT_APP_SOCKET_URL: process.env.REACT_APP_SOCKET_URL,
    REACT_APP_BRAND_SUPPORT_URL: process.env.REACT_APP_BRAND_SUPPORT_URL,
    REACT_APP_IPFS_GATEWAY_URL: process.env.REACT_APP_IPFS_GATEWAY_URL,
    REACT_APP_API_KEY: process.env.REACT_APP_API_KEY,
    REACT_APP_BRANDS_NORDIC_INTEGRATION_SETUP_TUTORIAL:
      process.env.REACT_APP_BRANDS_NORDIC_INTEGRATION_SETUP_TUTORIAL,
  });

  const params = new URLSearchParams(window.location.search);
  const message = params.get("message");
  const brandId = params.get("brand_id");
  const localeId = params.get("locale_id");
  const returnTo = params.get("return_to");

  useEffect(() => {
    const isSignedOut = message?.toLowerCase()?.includes("signed out") ?? false;

    if (!isSignedOut && (brandId || localeId || returnTo)) {
      sessionStorage.setItem(
        "externalRedirectData",
        JSON.stringify({
          brandId,
          localeId,
          returnTo,
          fromExternal: true,
        }),
      );
      if (returnTo) {
        capturePosthogEvent(AnalyticsEvents.RETURNED_FROM_HELP_CENTER, {
          previous_url: returnTo,
          user_id: brandId ? brandId : "",
        });
      }
    }
  }, [brandId, localeId, returnTo, message]);

  return (
    <DynamicContextProvider
      settings={{
        environmentId: Env.ENVIRONMENT_ID,
        walletConnectors: [EthereumWalletConnectors],
        overrides: {
          evmNetworks: [
            {
              blockExplorerUrls: [Env.AMOY_BLOCK_EXPLORER_URL],
              chainId: 80002,
              chainName: "Polygon Amoy",
              iconUrls: [Env.AMOY_ICON_URL],
              name: "Polygon Amoy",
              nativeCurrency: {
                decimals: 18,
                name: "MATIC",
                symbol: "MATIC",
              },
              networkId: 80002,
              rpcUrls: [Env.ALCHEMY_RPC_URL_WITH_KEY],
              vanityName: "Polygon Amoy",
            },
            {
              blockExplorerUrls: [Env.ETH_BLOCK_EXPLORER_URL],
              chainId: 1,
              chainName: "Ethereum Mainnet",
              iconUrls: [Env.ETH_ICON_URL],
              name: "Ethereum",
              nativeCurrency: {
                decimals: 18,
                name: "Ether",
                symbol: "ETH",
              },
              networkId: 1,
              rpcUrls: [Env.ETH_RPC_URL],
              vanityName: "Ethereum",
            },
          ],
        },
      }}
    >
      <>
        <Loader />
        <RoutesManager />
        <ViewportGuard />
        <ToastContainer
          newestOnTop
          autoClose={3000}
          hideProgressBar
          closeButton={false}
        />
      </>
    </DynamicContextProvider>
  );
};

export default App;
